/**
 * /payment/reservepay — Full-page ReservePay payment flow
 *
 * Supports multiple payment methods:
 *   ?method=promptpay (default) — QR scan flow
 *   ?method=card — credit/debit card form
 *   ?method=mobile_banking — bank selector → deeplink redirect
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DashboardLayout } from '@/components/dashboard/dashboard-layout'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, CheckCircle2, XCircle, Clock, ArrowLeft } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useReservePay } from '@/src/hooks/useReservePay'
import { CardForm } from '@/src/components/payment/CardForm'
import { BankSelector } from '@/src/components/payment/BankSelector'
import type { MobileBankCode } from '@/lib/payment-flags'
import {
  initiateCreditReservePayFlow,
  initiatePackageReservePayFlow,
  redirectToReservePayCheckout,
} from '@/lib/api/reservepay-payment'
import { useAuth } from '@/src/contexts/AuthContext'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PageState = 'bank_select' | 'card_form' | 'selecting' | 'initiating' | 'qr' | 'success' | 'failed'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ReservePayPaymentPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, refreshCredits } = useAuth()

  const amount = Number(searchParams.get('amount')) || 0
  const type = (searchParams.get('type') as 'credit' | 'package') || 'credit'
  const packageId = searchParams.get('packageId') || undefined
  const method = searchParams.get('method') || 'promptpay'

  const getInitialState = (): PageState => {
    if (method === 'card') return 'card_form'
    if (method === 'mobile_banking') return 'bank_select'
    return 'selecting'
  }

  const [state, setState] = useState<PageState>(getInitialState())
  const [qrString, setQrString] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [creditAmount, setCreditAmount] = useState<number | undefined>(undefined)
  const [_mobileBankCode, setMobileBankCode] = useState<MobileBankCode | null>(null)

  const abortRef = useRef<AbortController | null>(null)
  const { selectPromptPay, waitForQR, waitForCompletion, waitForCardAction } = useReservePay()

  // ── Shared flow runner — called by both PromptPay (auto) & CARD (on tokenize) ──
  async function runPaymentFlow(paymentSessionId: string) {
    const abort = new AbortController()
    abortRef.current = abort

    try {
      // Step 2: Initiate flow
      setState('initiating')
      let txId: string
      let creditAmt: number | undefined

      if (type === 'credit') {
        const res = await initiateCreditReservePayFlow(paymentSessionId, amount, {
          paymentMethodScope: method === 'card' ? 'card' : method === 'mobile_banking' ? 'mobile_banking' : method === 'true_money' ? 'true_money' : method === 'alipay' ? 'alipay' : method === 'wechat' ? 'wechat' : 'promptpay'
        })
        txId = res.data.transaction.id
        creditAmt = res.data.transaction.credit_amount
      } else {
        if (!packageId) throw new Error('packageId is required')
        const res = await initiatePackageReservePayFlow(paymentSessionId, packageId, undefined, {
          paymentMethodScope: method === 'card' ? 'card' : method === 'mobile_banking' ? 'mobile_banking' : method === 'true_money' ? 'true_money' : method === 'alipay' ? 'alipay' : method === 'wechat' ? 'wechat' : 'promptpay'
        })
        txId = res.data.transaction.id
      }
      if (abort.signal.aborted) return

      setCreditAmount(creditAmt)

      if (method === 'card' || method === 'mobile_banking' || method === 'true_money') {
        // CARD / MOBILE_BANKING / TRUE_MONEY: discover → REDIRECT or CHECK_STATUS
        const cardAction = await waitForCardAction(paymentSessionId)
        if (abort.signal.aborted) return

        if (cardAction.action === 'REDIRECT') {
          redirectToReservePayCheckout(cardAction.redirectUrl, txId, type)
          return // page unloads
        }

        // No redirect needed — poll status directly
        const finalStatus = await waitForCompletion(paymentSessionId, txId, abort.signal)
        if (abort.signal.aborted) return
        if (finalStatus === 'SUCCESSFUL') {
          setState('success')
          refreshCredits?.()
          toast.success(`ชำระเงินสำเร็จ${creditAmt ? ` +${creditAmt.toLocaleString()} เครดิต` : ''}`)
        } else {
          setState('failed')
          setErrorMsg('การชำระเงินไม่สำเร็จ')
        }
      } else {
        // PROMPTPAY: discover next action → QR
        const { qrString: qr } = await waitForQR(paymentSessionId, abort.signal)
        if (abort.signal.aborted) return
        setQrString(qr)
        setState('qr')

        const finalStatus = await waitForCompletion(paymentSessionId, txId, abort.signal)
        if (abort.signal.aborted) return

        if (finalStatus === 'SUCCESSFUL') {
          setState('success')
          refreshCredits?.()
          toast.success(`ชำระเงินสำเร็จ${creditAmt ? ` +${creditAmt.toLocaleString()} เครดิต` : ''}`)
        } else {
          setState('failed')
          setErrorMsg('การชำระเงินไม่สำเร็จ')
        }
      }
    } catch (err: any) {
      if (abort.signal.aborted) return
      console.error('[ReservePayPayment] error:', err)
      setState('failed')
      setErrorMsg(err.message || 'เกิดข้อผิดพลาด')
    }
  }

  // ── CARD: called by CardForm after tokenize + select ──
  function handleCardSubmit(paymentSessionId: string) {
    runPaymentFlow(paymentSessionId)
  }

  // ── MOBILE BANKING: called by BankSelector after bank pick ──
  function handleMobileBankingSubmit(paymentSessionId: string, bankCode: MobileBankCode) {
    setMobileBankCode(bankCode)
    runPaymentFlow(paymentSessionId)
  }

  // ── Auto-start for PROMPTPAY on mount ───────────────────────────────────
  useEffect(() => {
    if (!amount || amount <= 0) {
      navigate('/user/add-credit', { replace: true })
      return
    }

    if (method === 'card' || method === 'mobile_banking') return // Wait for user input

    // TrueMoney / Alipay / WeChat — auto-start (no UI selection needed)
    if (method === 'true_money' || method === 'alipay' || method === 'wechat') {
      const abort = new AbortController()
      abortRef.current = abort
      ;(async () => {
        try {
          setState('selecting')
          const token = localStorage.getItem('authToken')
          if (!token) throw new Error('Authentication required')
          const API_BASE = import.meta.env.VITE_API_BASE_URL || ''
          const MERCHANT_ID = import.meta.env.VITE_RESERVEPAY_MERCHANT_ID || ''
          const INSTALLATION_ID = import.meta.env.VITE_RESERVEPAY_INSTALLATION_ID || ''
          const amountSatang = String(Math.round(amount * 100))
          const pm = method === 'true_money' ? 'TRUE_MONEY' : method === 'alipay' ? 'ALIPAY' : 'WECHAT'

          const selectRes = await fetch(`${API_BASE}/api/payment/user/reservepay/select-payment-method`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ merchant_id: MERCHANT_ID, installation_id: INSTALLATION_ID, amount: amountSatang, currency: 'THB', payment_method: pm }),
          })
          const selectData = await selectRes.json()
          if (!selectRes.ok || !selectData?.success) throw new Error(selectData?.error || `Failed to select ${pm}`)
          const paymentSessionId = selectData?.data?.payment_session_id
          if (!paymentSessionId) throw new Error('No payment session ID')

          await runPaymentFlow(paymentSessionId)
        } catch (err: any) {
          if (abort.signal.aborted) return
          console.error('[ReservePayPayment] error:', err)
          setState('failed')
          setErrorMsg(err.message || 'เกิดข้อผิดพลาด')
        }
      })()
      return () => abort.abort()
    }

    const abort = new AbortController()
    abortRef.current = abort

    ;(async () => {
      try {
        setState('selecting')
        const { paymentSessionId } = await selectPromptPay(amount, {
          email: user?.email,
          mobile: user?.mobile,
          name: user?.shopName,
        })
        if (abort.signal.aborted) return
        await runPaymentFlow(paymentSessionId)
      } catch (err: any) {
        if (abort.signal.aborted) return
        console.error('[ReservePayPayment] error:', err)
        setState('failed')
        setErrorMsg(err.message || 'เกิดข้อผิดพลาด')
      }
    })()

    return () => abort.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Render ──────────────────────────────────────────────────────────────
  function renderContent() {
    switch (state) {
      case 'bank_select':
        return (
          <div className="py-6">
            <BankSelector
              amount={amount}
              onSubmit={handleMobileBankingSubmit}
              onCancel={() => navigate(-1)}
            />
          </div>
        )

      case 'card_form':
        return (
          <div className="py-6">
            <CardForm
              amount={amount}
              onSubmit={handleCardSubmit}
              onCancel={() => navigate(-1)}
            />
          </div>
        )

      case 'selecting':
      case 'initiating':
        return (
          <div className="flex flex-col items-center gap-6 py-16">
            <Loader2 className="w-12 h-12 animate-spin text-[#7FB069]" />
            <div className="text-center space-y-2">
              <h2 className="text-xl font-bold text-gray-800">
                {state === 'selecting' ? 'กำลังเตรียม PromptPay...' : 'กำลังสร้างรายการชำระเงิน...'}
              </h2>
              <p className="text-gray-500 text-sm">กรุณารอสักครู่</p>
            </div>
          </div>
        )

      case 'qr':
        return (
          <div className="flex flex-col items-center gap-6 py-8">
            <div className="text-center">
              <p className="text-sm text-gray-500">จำนวนเงิน</p>
              <p className="text-3xl font-bold text-[#7FB069]">
                ฿{amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
              </p>
              {type === 'credit' && creditAmount && (
                <p className="text-sm text-gray-500 mt-0.5">
                  ≈ {creditAmount.toLocaleString()} เครดิต
                </p>
              )}
            </div>

            {qrString && (
              <div className="p-6 bg-white rounded-2xl border-2 border-[#7FB069]/30 shadow-lg">
                <QRCodeSVG value={qrString} size={260} level="M" />
              </div>
            )}

            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-5 py-3 rounded-xl border border-amber-200 max-w-sm text-center">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <span>สแกน QR ด้วยแอปธนาคาร แล้วรอระบบยืนยันอัตโนมัติ</span>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>รอการยืนยันการชำระเงิน...</span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-gray-400 mt-4"
            >
              ยกเลิก
            </Button>
          </div>
        )

      case 'success':
        return (
          <div className="flex flex-col items-center gap-6 py-16">
            <CheckCircle2 className="w-20 h-20 text-green-500" />
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-green-700">ชำระเงินสำเร็จ!</h2>
              {type === 'credit' && creditAmount ? (
                <>
                  <p className="text-gray-600 text-lg">
                    เติม <span className="font-semibold text-[#7FB069]">{creditAmount.toLocaleString()} เครดิต</span> เข้าบัญชีของคุณแล้ว
                  </p>
                  <p className="text-sm text-gray-400">
                    จำนวนเงิน {amount.toLocaleString()} บาท
                  </p>
                </>
              ) : (
                <p className="text-gray-600">แพ็กเกจของคุณจะเริ่มใช้งานเร็วๆ นี้</p>
              )}
            </div>
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => navigate(type === 'credit' ? '/user/add-credit' : '/settings/packages')}
              >
                {type === 'credit' ? 'เติมเครดิตอีกครั้ง' : 'ดูแพ็กเกจ'}
              </Button>
              <Button
                className="bg-[#7FB069] hover:bg-[#6a9457] text-white"
                onClick={() => navigate('/dashboard')}
              >
                กลับหน้าหลัก
              </Button>
            </div>
          </div>
        )

      case 'failed':
        return (
          <div className="flex flex-col items-center gap-6 py-16">
            <XCircle className="w-20 h-20 text-red-500" />
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-red-700">
                {type === 'credit' ? 'การเติมเครดิตล้มเหลว' : 'การซื้อแพ็กเกจล้มเหลว'}
              </h2>
              {errorMsg && <p className="text-gray-500">{errorMsg}</p>}
            </div>
            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                กลับหน้าหลัก
              </Button>
              <Button
                className="bg-[#7FB069] hover:bg-[#6a9457] text-white"
                onClick={() => {
                  setState('selecting')
                  setQrString(null)
                  setErrorMsg(null)
                  setCreditAmount(undefined)
                  window.location.reload()
                }}
              >
                ลองใหม่
              </Button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto py-8 px-4">
        {/* Back button — hide during loading */}
        {state !== 'selecting' && state !== 'initiating' && state !== 'card_form' && state !== 'bank_select' && (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-gray-400 hover:text-gray-600 mb-6 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            ย้อนกลับ
          </button>
        )}
        <Card className="p-8">{renderContent()}</Card>
      </div>
    </DashboardLayout>
  )
}
